import type {SetStateAction} from 'react';
import type {ChatMessage, ChatMessageModelImagePart, ChatMessagePart, ControlStreamEvent, ModelMessage} from '@Components/ai-chat-window/ai-chat-window.types';
import type {HistoryItem} from '@AiChat/ai-chat.types';
import {ChatMessagePartType, ControlEventType, HandOffTargetKind, MessageRole} from '@Components/ai-chat-window/ai-chat-window.types';
import {appendSkeletonToMessage, getLastThinkingMessage, isComponentPart, replaceThinkingOrAppendMessage, replaceThinkingOrUpdateMessage} from '@Components/ai-chat-window/lib/ai-chat-stream.library';
import {createComponentPart} from '@Components/ai-chat-window/lib/stream-component-registry';
import {getSkeletonDimensions} from '@Components/ai-chat-window/lib/chat-attachment-image.library';
import {MAX_CHAT_IMAGE_DIMENSION} from '@Components/ai-chat-window/components/generated-image/generated-image.types';
import {showPurchaseCreditsDialog} from '@Libraries/ai-chat.library';
import {shareAiChatImage, shareUserDesign} from '@Components/ai-chat-window/lib/share-design.library';
import {
  postDesignOnSocialMedia,
  postUserDesignOnSocialMedia,
  promoteDesignAsEvent,
  promoteUserDesignAsEvent,
  sendDesignAsEmail,
  sendUserDesignAsEmail,
} from '@Components/ai-chat-window/lib/publish-design.library';
import {confirmUserDesignSaved, saveAiChatImageToMyDesigns} from '@Components/ai-chat-window/lib/save-design.library';
import {getUniqueString} from '@Utils/string.util';
import {logHandOffFailure} from '@Components/ai-chat-window/lib/hand-off-failure.library';
import {assertNever} from '@Utils/assert-never.util';
import {PublishedAssetKind, type AiChatCallbacks} from '@AiChat/callbacks/ai-chat-callbacks.types';
import {PublishingOption} from '@Components/ai-chat-window/components/publishing-options/publishing-options.types';
import {firePublishingOptionChosen} from '@Components/ai-chat-window/lib/ai-chat-callback-events.library';

export interface ControlEventHandlerDeps {
  debouncedRefreshChatHistory: () => void;
  onNewChat?: () => void;
  onUpdateHistoryItem?: (chatId: string, updates: Partial<HistoryItem>) => void;
  onSetSearchTags?: (chatId: string, tags: string[]) => void;
  getCurrentMessages: (chatId: string | undefined) => Record<string, ChatMessage>;
  setMessages: (chatId: string, updater: SetStateAction<Record<string, ChatMessage>>) => void;
  refreshCredits?: () => void;
  applyMessageEdit: (chatId: string, editedMessageId: string, historyFromServer: ChatMessage[]) => void;
  openPosterInEditor: (hashedId: string) => void;
  openUserDesignInEditor: (posterHashedId: string) => void;
  clearPinnedAspectRatio: (droppedAspectRatio: string) => void;
  callbacks: AiChatCallbacks;
}

type ControlEventHandler = (event: ControlStreamEvent, deps: ControlEventHandlerDeps) => void;

const isImageSkeleton = (part: ChatMessagePart): boolean =>
  part.type === ChatMessagePartType.MODEL_GENERATED_IMAGE && 'isSkeletonPlaceholder' in part && Boolean(part.isSkeletonPlaceholder);

const skeletonMessage = (part: ChatMessagePart, isThinking: boolean): ModelMessage => ({
  role: MessageRole.ASSISTANT,
  isThinking,
  parts: [part],
  messageId: getUniqueString(),
  feedback: null,
  usage: {inputTokens: 0, outputTokens: 0},
});

const newChat: ControlEventHandler = (_event, deps) => {
  deps.debouncedRefreshChatHistory();
  deps.onNewChat?.();
};

const thumbnailUrl: ControlEventHandler = (event, deps) => {
  if (!event.idAiChat || !event.data?.assetHashedFilename) {
    return;
  }

  deps.onUpdateHistoryItem?.(event.idAiChat, {assetHashedFilename: event.data.assetHashedFilename});
};

const chatTitle: ControlEventHandler = (event, deps) => {
  if (!event.idAiChat || !event.data?.chatTitle) {
    return;
  }

  deps.onUpdateHistoryItem?.(event.idAiChat, {title: event.data.chatTitle});
};

const searchTags: ControlEventHandler = (event, deps) => {
  if (!event.idAiChat || !event.data?.searchTags) {
    return;
  }

  deps.onSetSearchTags?.(event.idAiChat, event.data.searchTags);
};

const imageSkeleton: ControlEventHandler = (event, deps) => {
  const chatId = event.idAiChat;
  const currentMessages = deps.getCurrentMessages(chatId);
  const dimensions = getSkeletonDimensions(event.data?.width, event.data?.height, event.data?.aspectRatio, MAX_CHAT_IMAGE_DIMENSION);

  const part: ChatMessageModelImagePart = {
    type: ChatMessagePartType.MODEL_GENERATED_IMAGE,
    hashedId: '',
    width: dimensions.width,
    height: dimensions.height,
    isSkeletonPlaceholder: true,
    isNewGeneration: true,
  };

  const thinkingMessage = getLastThinkingMessage(currentMessages);

  if (thinkingMessage?.messageId) {
    const updated = appendSkeletonToMessage(thinkingMessage, part, isImageSkeleton);
    deps.setMessages(chatId, {...currentMessages, [thinkingMessage.messageId]: updated});
    return;
  }

  const fresh = replaceThinkingOrUpdateMessage(currentMessages, skeletonMessage(part, false), (existing) => appendSkeletonToMessage(existing, part, isImageSkeleton));
  deps.setMessages(chatId, fresh);
};

const entitySkeleton: ControlEventHandler = (event, deps) => {
  const componentType = event.data?.componentType;

  if (!componentType) {
    return;
  }

  const part = createComponentPart({componentType, props: {mode: event.data?.mode, buttonWidths: event.data?.buttonWidths}});

  if (!part) {
    return;
  }

  const chatId = event.idAiChat;
  const currentMessages = deps.getCurrentMessages(chatId);
  const matchesComponentType = (candidate: ChatMessagePart): boolean => isComponentPart(candidate, part.componentType);
  const thinkingMessage = getLastThinkingMessage(currentMessages);

  if (thinkingMessage?.messageId) {
    const updated = appendSkeletonToMessage(thinkingMessage, part, matchesComponentType);
    deps.setMessages(chatId, {...currentMessages, [thinkingMessage.messageId]: updated});
    return;
  }

  deps.setMessages(chatId, replaceThinkingOrAppendMessage(currentMessages, skeletonMessage(part, true)));
};

const creditsRefresh: ControlEventHandler = (_event, deps) => {
  deps.refreshCredits?.();
};

const messageEdit: ControlEventHandler = (event, deps) => {
  const editedMessageId = event.data?.editedMessageId;
  if (!event.idAiChat || !editedMessageId) {
    return;
  }

  deps.applyMessageEdit(event.idAiChat, editedMessageId, event.data?.history ?? []);
};

const insufficientCredits: ControlEventHandler = () => {
  void showPurchaseCreditsDialog();
};

interface UserDesignTarget {
  posterHashedId: string;
  pageHashedId: string | null;
}

type HandOffTarget = {kind: HandOffTargetKind.DESIGN; userDesignTarget: UserDesignTarget} | {kind: HandOffTargetKind.IMAGE; imageHashedId: string};

/** One runner per kind: adding a HandOffTargetKind is a compile error in every handler until it is handled here. */
interface HandOffRunners<Result> {
  [HandOffTargetKind.DESIGN]: (target: UserDesignTarget) => Result;
  [HandOffTargetKind.IMAGE]: (imageHashedId: string) => Result;
}

const isHandOffTargetKind = (candidate: unknown): candidate is HandOffTargetKind => {
  return Object.values<unknown>(HandOffTargetKind).includes(candidate);
};

/** The event names its target kind; a missing, unknown or field-less target resolves to nothing. */
const resolveHandOffTarget = (event: ControlStreamEvent): HandOffTarget | null => {
  const targetKind = event.data?.targetKind;
  if (!isHandOffTargetKind(targetKind)) {
    return null;
  }

  switch (targetKind) {
    case HandOffTargetKind.DESIGN: {
      const posterHashedId = event.data?.posterHashedId;
      return posterHashedId ? {kind: targetKind, userDesignTarget: {posterHashedId, pageHashedId: event.data?.pageHashedId ?? null}} : null;
    }
    case HandOffTargetKind.IMAGE: {
      const imageHashedId = event.data?.imageHashedId;
      return imageHashedId ? {kind: targetKind, imageHashedId} : null;
    }
    default:
      return assertNever(targetKind);
  }
};

const runForHandOffTarget = <Result>(handOffTarget: HandOffTarget, runners: HandOffRunners<Result>): Result => {
  switch (handOffTarget.kind) {
    case HandOffTargetKind.DESIGN:
      return runners[HandOffTargetKind.DESIGN](handOffTarget.userDesignTarget);
    case HandOffTargetKind.IMAGE:
      return runners[HandOffTargetKind.IMAGE](handOffTarget.imageHashedId);
    default:
      return assertNever(handOffTarget);
  }
};

const handOffHandler = (runners: HandOffRunners<Promise<void> | void>): ControlEventHandler => {
  return (event) => {
    const handOffTarget = resolveHandOffTarget(event);
    if (!handOffTarget) {
      return;
    }

    logHandOffFailure(Promise.resolve(runForHandOffTarget(handOffTarget, runners)));
  };
};

const share = handOffHandler({
  [HandOffTargetKind.DESIGN]: ({posterHashedId}) => {
    shareUserDesign(posterHashedId);
  },
  [HandOffTargetKind.IMAGE]: shareAiChatImage,
});

/** An agent-triggered publish tells the host which option was chosen, for a chat image and for the user's own design, exactly as a click on the publish menu does. */
const publishActionHandler = (option: PublishingOption, publishers: HandOffRunners<Promise<void> | void>): ControlEventHandler => {
  return (event, deps) => {
    const publishUserDesignAndReportOption = async (target: UserDesignTarget): Promise<void> => {
      firePublishingOptionChosen(deps.callbacks, {kind: PublishedAssetKind.DESIGN, chatId: event.idAiChat, option, ...target});
      await publishers[HandOffTargetKind.DESIGN](target);
    };
    const publishImageAndReportOption = async (imageHashedId: string): Promise<void> => {
      firePublishingOptionChosen(deps.callbacks, {kind: PublishedAssetKind.IMAGE, chatId: event.idAiChat, option, imageHashedId});
      await publishers[HandOffTargetKind.IMAGE](imageHashedId);
    };
    handOffHandler({
      [HandOffTargetKind.DESIGN]: publishUserDesignAndReportOption,
      [HandOffTargetKind.IMAGE]: publishImageAndReportOption,
    })(event, deps);
  };
};

const postOnSocial = publishActionHandler(PublishingOption.PUBLISH_TO_SOCIAL_MEDIA, {
  [HandOffTargetKind.DESIGN]: ({posterHashedId}) => postUserDesignOnSocialMedia(posterHashedId),
  [HandOffTargetKind.IMAGE]: postDesignOnSocialMedia,
});
const promoteAsEvent = publishActionHandler(PublishingOption.PROMOTE_AS_EVENT, {
  [HandOffTargetKind.DESIGN]: ({posterHashedId, pageHashedId}) => promoteUserDesignAsEvent(posterHashedId, pageHashedId),
  [HandOffTargetKind.IMAGE]: promoteDesignAsEvent,
});
const sendAsEmail = publishActionHandler(PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN, {
  [HandOffTargetKind.DESIGN]: ({posterHashedId, pageHashedId}) => sendUserDesignAsEmail(posterHashedId, pageHashedId),
  [HandOffTargetKind.IMAGE]: sendDesignAsEmail,
});
const saveToMyDesigns = handOffHandler({
  [HandOffTargetKind.DESIGN]: confirmUserDesignSaved,
  [HandOffTargetKind.IMAGE]: saveAiChatImageToMyDesigns,
});

const aspectRatioPinDropped: ControlEventHandler = (event, deps) => {
  const droppedAspectRatio = event.data?.aspectRatio;
  if (!droppedAspectRatio) {
    return;
  }

  deps.clearPinnedAspectRatio(droppedAspectRatio);
};

const openInEditor: ControlEventHandler = (event, deps) => {
  const handOffTarget = resolveHandOffTarget(event);
  if (!handOffTarget) {
    return;
  }

  runForHandOffTarget(handOffTarget, {
    [HandOffTargetKind.DESIGN]: ({posterHashedId}) => {
      deps.openUserDesignInEditor(posterHashedId);
    },
    [HandOffTargetKind.IMAGE]: deps.openPosterInEditor,
  });
};

const CONTROL_EVENT_HANDLERS: Partial<Record<ControlEventType, ControlEventHandler>> = {
  [ControlEventType.NEW_CHAT]: newChat,
  [ControlEventType.THUMBNAIL_URL]: thumbnailUrl,
  [ControlEventType.CHAT_TITLE]: chatTitle,
  [ControlEventType.SEARCH_TAGS]: searchTags,
  [ControlEventType.IMAGE_STREAMING_START]: imageSkeleton,
  [ControlEventType.ENTITY_SKELETON_START]: entitySkeleton,
  [ControlEventType.CREDITS_REFRESH]: creditsRefresh,
  [ControlEventType.MESSAGE_EDIT_APPLIED]: messageEdit,
  [ControlEventType.INSUFFICIENT_CREDITS]: insufficientCredits,
  [ControlEventType.SHARE]: share,
  [ControlEventType.OPEN_IN_EDITOR]: openInEditor,
  [ControlEventType.POST_ON_SOCIAL]: postOnSocial,
  [ControlEventType.PROMOTE_AS_EVENT]: promoteAsEvent,
  [ControlEventType.SEND_AS_EMAIL]: sendAsEmail,
  [ControlEventType.SAVE_TO_MY_DESIGNS]: saveToMyDesigns,
  [ControlEventType.ASPECT_RATIO_PIN_DROPPED]: aspectRatioPinDropped,
};

export function dispatchControlEvent(event: ControlStreamEvent, deps: ControlEventHandlerDeps): void {
  CONTROL_EVENT_HANDLERS[event.type]?.(event, deps);
}
